// A card with a toolbar and one stacked line/area chart over time buckets. Series are
// **colour bands**: every distinct `color` in the rows is one series, named through
// `bandTitles`. The component owns bucket switching, zoom, the lazy per-bucket tooltip,
// the PNG export and the reload lifecycle — and knows nothing about statuses, templates
// or tables (rules 1–2). It replaces the app's two StatusHistory line charts, which
// differed only in `mode`.
//
//   props:  title             (String)   card title, rendered as-is — already translated
//           infoText          (String)   mdi-information tooltip text; the icon is hidden when empty
//           mode              (String)   'periodic' (per-bucket counts) | 'cumulative' (running totals)
//           groupBy           (String)   'day'|'week'|'month'; supports v-model:groupBy; null = the mode default
//           groupByOptions    (Array)    which toggle buttons render
//           bandTitles        (Object)   { [color]: 'Open, In Progress' } — the series/legend name per band
//           exportFilename    (String)   passed to downloadChartAsPNG
//           exportTitle       (String)   the PNG's title; '' = `title` (2026-10-16)
//           showToolbar       (Boolean)  shows the PNG download button (class `rdrive-chart-export`)
//           filterContext     (Object|Array) the filter-context duck type, or an array of them
//                                        (reload on a tick from any)
//           dataLoaded        (Boolean)  gates the initial load
//           fetchSeries       (Function) (groupBy) => Promise<Array<{ timeKey, color, value, sort }>>
//           fetchTooltip      (Function) (timeKey, groupBy) => Promise<Array<{ title, color, sort?, count }>>
//                                        — needed unless `stacked` is false
//           fetchLegendTitles (Function) (timeKeys, groupBy) => Promise<Array<{ color, title }>>, PNG legend
//           seriesType        (String)   'line' (default) | 'bar' — opt-in stacked bars (periodic)
//           bandOrder         (String)   'asc' | 'desc' | null (= the mode's) — opt-in stacking order
//           resolveGroupBy    (Function) () => Promise<'day'|'week'|'month'|null> — opt-in: picks the
//                                        bucket before the first load after dataLoaded turns true
//           stacked           (Boolean)  true (default) | false — opt-in unstacked bands (each its own
//                                        line over a faint area) and a plotted-value tooltip
//           showLegend        (Boolean)  opt-in legend above the plot
//           todayMarker       (Boolean)  opt-in dashed "Today" line on today's bucket
//           shadeFuture       (Boolean)  opt-in shade from today's bucket to the last
//   emits:  update:groupBy
//   setup:  downloadPng()  the download button's action (= `downloadChart`), callable through a
//                          template ref like every chart base's
//   strings: ./messages — day/week/month/total/loading/resetZoom/noData/unknown and, since D4,
//            `today` (the marker's label)
//
// There is deliberately NO `chartFontColor` prop: every colour comes from
// `useChartTheme()` and the option is rebuilt on a theme change, so a toggle repaints
// without a reload (PLATFORM.md §5 Phase 1 step 2 — this is the first base to retire it).
//
// Full contract: ../../../README.md → BaseTimeSeriesChart. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below — both
// are what the template's `<v-chart>` needs — so it lives here and ./setup.js stays
// pure logic. `ToolboxComponent` was registered by the two SFCs this component replaced, but
// nothing in the option ever used it, so it is dropped. `BarChart` is back since IA_ABSORPTION_PLAN
// 12c, for the opt-in `seriesType="bar"` (@rdrive/helper-chart-export registers it globally
// anyway, so line-only consumers see no change). `MarkLineComponent` / `MarkAreaComponent` since
// AM_ABSORPTION_PLAN step D4, for the opt-in `todayMarker` / `shadeFuture` (inert without a
// series `markLine` / `markArea`; `use()` is global, so the PNG export's off-screen chart draws
// them too).
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart } from "echarts/charts";
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkAreaComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkAreaComponent,
]);

export default {
  name: 'base-time-series-chart',
  props: {
    // Rendered as-is — already translated by the page
    title: {
      type: String,
      default: ''
    },
    // Text of the mdi-information tooltip next to the title; empty hides the icon
    infoText: {
      type: String,
      default: ''
    },
    // 'periodic': values are per-bucket counts. 'cumulative': values are running totals
    mode: {
      type: String,
      default: 'periodic',
      validator: (v) => ['periodic', 'cumulative'].includes(v)
    },
    // null/undefined = the mode's default bucket ('week' periodic, 'month' cumulative)
    groupBy: {
      type: String,
      default: null,
      validator: (v) => v === null || ['day', 'week', 'month'].includes(v)
    },
    // Which toggle buttons render, in order
    groupByOptions: {
      type: Array,
      default: () => ['day', 'week', 'month']
    },
    // { [color]: 'Open, In Progress' } — a colour with no entry is named by its value
    bandTitles: {
      type: Object,
      default: () => ({})
    },
    exportFilename: {
      type: String,
      default: 'chart.png'
    },
    // The PNG's title (2026-10-16: every chart PNG carries its card title); '' = `title`
    exportTitle: {
      type: String,
      default: ''
    },
    // Shows the PNG download button (the bucket toggle and reset stay either way)
    showToolbar: {
      type: Boolean,
      default: true
    },
    // One filter-context duck type, or an ARRAY of them: the chart reloads on a tick from
    // any (issue-analysis's velocity reads both of its contexts)
    filterContext: {
      type: [Object, Array],
      required: true
    },
    dataLoaded: {
      type: Boolean,
      required: true
    },
    // (groupBy) => Promise<Array<{ timeKey, color, value, sort }>>
    fetchSeries: {
      type: Function,
      required: true
    },
    // (timeKey, groupBy) => Promise<Array<{ title, color, sort, count }>>. Needed by every
    // STACKED chart (the default); `stacked: false` never calls it (its tooltip reads the plotted
    // values), so it is not `required` since AM_ABSORPTION_PLAN step D4
    fetchTooltip: {
      type: Function,
      default: null
    },
    // (timeKeys, groupBy) => Promise<Array<{ color, title }>>, for the PNG legend;
    // when absent the export names bands from `bandTitles`
    fetchLegendTitles: {
      type: Function,
      default: null
    },
    // --- opt-in, all inert at their defaults (IA_ABSORPTION_PLAN Phase 4 step 12c) ---
    // 'bar': each band is a stacked bar instead of a line/area (meant for mode="periodic")
    seriesType: {
      type: String,
      default: 'line',
      validator: (v) => ['line', 'bar'].includes(v)
    },
    // The stacking order of the bands by `sort`; null = the mode's ('asc' periodic, 'desc' cumulative)
    bandOrder: {
      type: String,
      default: null,
      validator: (v) => v === null || ['asc', 'desc'].includes(v)
    },
    // () => Promise<'day'|'week'|'month'|null>: picks the bucket before the first load after
    // `dataLoaded` turns true (e.g. from the data's date span); null keeps the current bucket
    resolveGroupBy: {
      type: Function,
      default: null
    },
    // --- opt-in, all inert at their defaults (AM_ABSORPTION_PLAN step D4, asset-management's
    // Inspection Progress) ---
    // false: every band is its own line over a faint area (separate measures that overlap, not
    // parts of a whole), and the tooltip lists each band's plotted value — no total, no fetchTooltip
    stacked: {
      type: Boolean,
      default: true
    },
    // A legend above the plot: the bands' names, in the theme's font colour
    showLegend: {
      type: Boolean,
      default: false
    },
    // A dashed line labelled "Today" on the bucket holding today (UTC — DuckDB-WASM's CURRENT_DATE),
    // drawn only when a later bucket is on the axis
    todayMarker: {
      type: Boolean,
      default: false
    },
    // A faint shade from today's bucket to the last one (the same condition)
    shadeFuture: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:groupBy'],
  // `<v-chart>` was auto-registered from the import by `<script setup>`; a classic
  // component has to register it explicitly or Vue logs "Failed to resolve component:
  // v-chart" and renders nothing. Vuetify tags need no entry — vite-plugin-vuetify
  // rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
